import FuelAnalyticsService from '@/services/fuel-analytics-service';
import clientUtils from '@/utils/clientUtils';

import type { AppAnalyticsState, BaseAnalyticsData } from './AppAnalytics';
import { SnowflakeCache } from './SnowflakeCache';
import { DateTime } from 'luxon';

const cacheUtils = SnowflakeCache();

export enum SnowflakeQueries {
  generic = 'generic',
  topChannels = 'topChannels',
  content = 'content',
  contentByTop = 'contentByTop',
  liveChannel = 'liveChannel',
  pageViews = 'pageViews',
  uniqueVisitors = 'uniqueVisitors',
  videoViews = 'videoViews',
}

const AppAnalyticsSnowflakeService = () => {
  const FN_MAP = {
    generic: fetchGenericAnalytics,
    topChannels: fetchTopChannels,
    content: fetchContentAnalytics,
    contentByTop: fetchTopContentData,
    liveChannel: fetchLiveChannelAnalytics,
    pageViews: fetchPageViews,
    uniqueVisitors: fetchUniqueVisitors,
    videoViews: fetchVideoViews,
  }

  const getData = async (type: SnowflakeQueries, params: AppAnalyticsState) => {
    const handler = FN_MAP[type];

    if (!handler) {
      throw new Error(`No handler found for type: ${type}`);
    }

    const key = _composeKey(type, params);
    const cached = cacheUtils.get(key);
    if (cached) {
      if (cached === 'loading') {
        // a request is already in progress
        return await _retryInflightRequest(type, params, 100);
      } else {
        return cached;
      }
    }

    cacheUtils.request(key)
    const data = await handler(params);
    cacheUtils.cache(key, data);

    return data;
  }

  return {
    getData,
  };
};

export { AppAnalyticsSnowflakeService };

// #region Individual Query Functions
async function fetchGenericAnalytics(params: AppAnalyticsState) {

  let query = `SELECT
                "site" AS "site",
                DATE_TRUNC('DAY', DATEADD(minutes, -480, "eventdate")) AS "eventdate",
                "eventtype",
                CASE 
                WHEN "devicetype" in ('web', 'undefined') THEN 'web'
                WHEN "devicetype" in ('firetv', 'fire_tv') THEN 'firetv'
                ELSE "devicetype"
                END as "devicetype",
                "countrycode",
                count(*) AS "views",
                count(distinct(coalesce("user", 'unauthenticated'))) as "uniqueviews",
                to_number(sum("params": content.duration), 29, 9) AS "duration"
              FROM ANALYTICS_STAGE.PUBLIC.EVENTS
              WHERE "eventtype" IN ('page', 'watch')
                AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
                AND "site" IN (${params.sites.map((site) => `'${site}'`).join(', ')})
              GROUP BY 
                "site",
                DATE_TRUNC('DAY', DATEADD(minutes, -480, "eventdate")),
                  "eventtype",
                  "devicetype",
                  "countrycode"
              ORDER BY 
                "site",
                  DATE_TRUNC('DAY', DATEADD(minutes, -480, "eventdate")),
                  "eventtype",
                  "devicetype",
                  "countrycode";`

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    return queryResults.data;
  }

  return [];
}
async function fetchTopChannels(params: AppAnalyticsState) {
  const query = `
            SELECT 
              "site",
              COALESCE("liveChannel", "channel") as "channel",
              count(*) AS "count",
              to_number(sum("params":content.duration), 29, 9) AS "duration",
              (
              CASE "count"
                WHEN 0
                THEN 0
                ELSE to_number("duration" / "count", 29, 9)
              END
              ) AS "average"
            FROM ANALYTICS_STAGE.PUBLIC.EVENTS
            WHERE "eventtype" IN ('watch', 'liveChannelWatch')
              AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
              AND "site" IN ('${params.sites.join("','")}')
              AND COALESCE("liveChannel", "channel") IS NOT NULL
            GROUP BY "site", COALESCE("liveChannel", "channel")
            ORDER BY "count" DESC
              LIMIT 10;`;

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    const sortedData = queryResults.data.reverse()
    return sortedData;
  }

  return [];
}
async function fetchContentAnalytics(params: AppAnalyticsState) {
  let query = `
      SELECT "video",
        count(*) AS "count",
        to_number(sum("params": content.duration), 29, 9) AS "duration",
        CASE "count" WHEN 0 THEN 0 ELSE to_number("duration" / "count", 29, 9) END AS "average"
      FROM ANALYTICS_STAGE.PUBLIC.EVENTS
      WHERE "site" IN(${params.sites.map((site) => `'${site}'`).join(', ')})
        AND "eventtype" = 'watch'
        AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
        AND "video" IS NOT NULL`;

  query += _getVideoWhereClause(params);

  query += `
      GROUP BY "video"
      ORDER BY "count" desc;`;

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    return queryResults.data;
  }

  return [];
};
async function fetchLiveChannelAnalytics(params: AppAnalyticsState) {
  let query = `
      SELECT "liveChannel" as "channel",
        count(*) AS "count",
        to_number(sum("params": content.duration), 29, 9) AS "duration",
        CASE "count" WHEN 0 THEN 0 ELSE to_number("duration" / "count", 29, 9) END AS "average"
      FROM ANALYTICS_STAGE.PUBLIC.EVENTS
      WHERE "site" IN(${params.sites.map((site) => `'${site}'`).join(', ')})
        AND "eventtype" = 'liveChannelWatch'
        AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
        AND "liveChannel" IS NOT NULL`;

  query += _getChannelWhereClause(params);

  query += `
      GROUP BY "liveChannel"
      ORDER BY "count" desc;`;

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    return queryResults.data;
  }

  return [];
};
async function fetchPageViews(params: AppAnalyticsState) {
  let eventTypes = ['page'];
  if (params.selectedChannels.length > 0) {
    eventTypes = ['video', 'liveChannel'];
  } else if (params.selectedItems.length > 0) {
    eventTypes = ['video'];
  }
  let query = `SELECT
            ${_getGroupByClause(params)} AS "site",
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset
    }, "eventdate")) AS "eventdate",
            COUNT(*) AS total
          FROM ANALYTICS_STAGE.PUBLIC.EVENTS
          WHERE "eventtype" IN ('${eventTypes.join("','")}')
            AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
            AND "site" IN ('${params.sites.join("','")}')`;

  query += _getAdditionalWhereClauses(params);

  query += `
          GROUP BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"))
          ORDER BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"));`;

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  // massage the data to get the UTC dates in timezone
  if (queryResults?.data?.length > 0) {
    const result = _correctDates(queryResults.data, params);
    return result;
  }

  return [];
}
async function fetchTopContentData(params: AppAnalyticsState) {
  let query = `SELECT
              "site",
              "video",
              LISTAGG(DISTINCT COALESCE("channel", 'NULL'), ',') WITHIN GROUP (ORDER BY COALESCE("channel", 'NULL')) AS "channels",
              count(*) AS "count",
              to_number(sum("params":content.duration), 29, 9) AS "duration",
              (
              CASE "count"
                WHEN 0
                THEN 0
                ELSE to_number("duration" / "count", 29, 9)
              END
              ) AS "average"
            FROM ANALYTICS_STAGE.PUBLIC.EVENTS
            WHERE "eventtype" = 'watch'
              AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
              AND "site" IN ('${params.sites.join("','")}')
              AND "video" IS NOT NULL`;

  query += `
            GROUP BY "site", "video"
            ORDER BY "count" DESC
              LIMIT 10;`;

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    return queryResults.data;
  }

  return [];
}
async function fetchUniqueVisitors(params: AppAnalyticsState) {
  let eventTypes = ['page'];
  if (params.selectedChannels.length > 0) {
    eventTypes = ['video', 'liveChannel', 'liveChannelWatch'];
  } else if (params.selectedItems.length > 0) {
    eventTypes = ['video'];
  }

  let query = `SELECT 
                ${_getGroupByClause(params)} AS "site",
                DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset
    }, "eventdate")) AS "eventdate",
                count(distinct(coalesce("user", 'unauthenticated'))) as "total"
              FROM ANALYTICS_STAGE.PUBLIC.EVENTS
              WHERE "eventtype" IN ('${eventTypes.join("','")}')
                AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
                AND "site" IN ('${params.sites.join("','")}')`;

  query += _getAdditionalWhereClauses(params);

  query += `
              GROUP BY ${_getGroupByClause(params)}, 
              DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"))
              ORDER BY ${_getGroupByClause(params)}, 
              DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"));`;

  // POST https://fueltools-dev01-v1-api.mediapipe.com/analytics/executequery?ouIds
  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    const results = _correctDates(queryResults.data, params);

    return results;
  }

  return [];
}
async function fetchVideoViews(params: AppAnalyticsState) {
  const eventTypes = ['watch', 'liveChannelWatch'];

  let query = `
          SELECT ${_getGroupByClause(params)} AS "site",
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset
    }, "eventdate")) AS "eventdate",
            count(*) AS "total",
            to_number(sum("params": content.duration), 29, 9) AS "duration",
            CASE "total" WHEN 0 THEN 0 ELSE to_number("duration" / "total", 29, 9) END AS "average"
          FROM ANALYTICS_STAGE.PUBLIC.EVENTS
          WHERE "eventtype" IN ('${eventTypes.join("','")}')
            AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
            AND "site" IN ('${params.sites.join("','")}')`;

  query += _getAdditionalWhereClauses(params);

  query += `
          GROUP BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"))
          ORDER BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"));`;

  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    const results = _correctDates(queryResults.data, params);

    return results;
  }

  return [];
}
// #endregion

// #region Helper Functions
function _retryInflightRequest(type: SnowflakeQueries, params: AppAnalyticsState, delay: number) {
  return new Promise((resolve) => {
    setTimeout(async () => {
      const service = AppAnalyticsSnowflakeService();
      const data = await service.getData(type, params);
      resolve(data);
    }, delay);
  });
}
function _correctDates(data: BaseAnalyticsData[], params: AppAnalyticsState) {
  for (let i = 0; i < data.length; i++) {
    data[i].eventdate = DateTime.fromISO(`${data[i].eventdate}.000Z`)
      .minus({ minutes: params.offset }) // Reverse the offset
      .toUTC()
      .toISO();
  }
  return data;
}

function _getAdditionalWhereClauses(params: AppAnalyticsState) {
  let additionalWhereClauses = '';
  additionalWhereClauses += _getVideoWhereClause(params);
  additionalWhereClauses += _getChannelWhereClause(params);
  return additionalWhereClauses;
}

function _getChannelWhereClause(params: AppAnalyticsState) {
  if (params.selectedChannels.length > 0) {
    return `
          AND ("channel" IN ('${params.selectedChannels.map((x) => x.id).join("','")}${params.selectedChannels
        .filter((x) => !clientUtils.isNullOrWhitespace(x.alias))
        .map((x) => x.alias)
        .join("','")}')
            OR "liveChannel" IN ('${params.selectedChannels.map((x) => x.id).join("','")}${params.selectedChannels
        .filter((x) => !clientUtils.isNullOrWhitespace(x.alias))
        .map((x) => x.alias)
        .join("','")}'))`;
  }
  return '';
};

function _getGroupByClause(params: AppAnalyticsState) {
  if (params.groupBy === 'channel') {
    return 'COALESCE("liveChannel", "channel")';
  }
  return `"${params.groupBy}"`;
};

function _getVideoWhereClause(params: AppAnalyticsState) {
  if (params.selectedItems.length > 0) {
    return `
          AND "video" IN ('${params.selectedItems.map((x) => x.id).join("','")}')`;
  }
  return '';
};

function _composeKey(prefix: string, params: AppAnalyticsState): string {
  let key = prefix + '_';
  key += `FROM:${params.fromDate}_TO:${params.toDate}_SITES:${params.sites.sort().join(',')}_CLIPS:${params.selectedItems.map((x) => x.id).join(',')}_CHANNELS${params.selectedChannels.map((x) => x.id).join(',')}`;

  return key
}
// #endregion